/**
 * Watermark layer generator for creating watermark-only images on transparent backgrounds
 */

import { 
    applyWatermark,
    convertServiceSettingsToWatermarkOptions,
    isWatermarkEnabled,
    blobToDataURL
} from './watermark';

/**
 * Create a watermark-only image on a transparent background
 * @param watermarkSettings - The watermark settings from the service
 * @param width - The width of the canvas
 * @param height - The height of the canvas
 * @returns A promise that resolves to an HTMLImageElement with the watermark, or null if watermark is disabled
 */
export async function createWatermarkOnlyImage(
    watermarkSettings: any,
    width: number,
    height: number
): Promise<HTMLImageElement | null> {
    if (!isWatermarkEnabled(watermarkSettings)) {
        return null;
    }

    // Create a transparent canvas of the specified size
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
        console.error('Failed to get canvas context');
        return null;
    }

    // Fill with transparent pixels
    ctx.clearRect(0, 0, width, height);
    
    // Convert the transparent canvas to a blob
    const transparentBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Failed to create transparent blob'));
            }
        }, 'image/png');
    });

    // Apply watermark using the existing function
    const options = convertServiceSettingsToWatermarkOptions(watermarkSettings);
    const result = await applyWatermark(transparentBlob, options);

    // Convert the result blob to a data URL
    const dataURL = await blobToDataURL(result.blob);

    // Create and return an Image element
    const img = new Image();
    img.src = dataURL;
    
    return new Promise((resolve) => {
        img.onload = () => {
            // Clean up the data URL after loading
            URL.revokeObjectURL(dataURL);
            resolve(img);
        };
        img.onerror = () => {
            console.error('Failed to load watermark image');
            resolve(null);
        };
    });
}